/**
 * T-15 — `change_ordinal` cursor + supersede (DS-1g-04 §4.6,
 * PIN-1e→1g-ORDINAL) — AC-1g-08.
 *
 * On EVERY status change (route-at-save re-route / undo-post-finalize /
 * decision resolve) the PWA must supply `change_ordinal`
 * (strictly-monotonic-per-canonical, base 0 on the canonical itself) +
 * `supersedes_ref`, per the 1e/1f carrier input contract (design §8.1:
 * "Status change / re-route / resolve / undo-post-finalize → carrier Event
 * per 1e input contract (change_ordinal, supersedes_ref)").
 *
 * Allocation: `next = max(highest ordinal in the collapsed read for that
 * canonical, highest this device has itself allocated for that canonical
 * so far) + 1`. The device-side half is a durable cursor (IDB `ordinals`
 * store — already provisioned in the schema by T-02's db.ts, unpopulated
 * until now) so an offline device keeps advancing its own local cursor
 * without a round-trip; the collapsed-read half is supplied by the caller
 * (T-16/T-17/T-18's read consumers), since only THEY see the server's
 * current highest ordinal for that canonical.
 *
 * `CarrierFields.change_ordinal` is a required (non-optional) field on a
 * type ONLY constructible via `buildCarrierFields()` below — there is no
 * other code path in this module (or any importer) that can produce a
 * `CarrierFields` value, so the 1e/1f fold's fail-closed
 * `MissingChangeOrdinal` rejection is structurally unreachable from any
 * PWA-emitted shape (AC-1g-08). It remains reachable only via a
 * hand-built, non-PWA-emittable fixture (the negative test in
 * ordinals.test.ts constructs exactly that, deliberately bypassing this
 * module, to document what the fail-closed path guards against).
 *
 * This module does NOT touch `outbox/db.ts` (a pre-existing file) — it
 * reuses `openOutboxDB()`'s existing exported handle and reads/writes the
 * `ordinals` store directly via the `idb` API, using a locally-declared
 * interface that is structurally identical to db.ts's private
 * `OrdinalRecord` (TypeScript structural typing; no shared import needed
 * or possible without a db.ts edit).
 */

import { openOutboxDB } from "../outbox/db";

/** Structurally identical to db.ts's private `OrdinalRecord` — the
 * `ordinals` IDB store (keyPath `canonical_ref`) already exists in the
 * schema; this is the first task to populate it. */
interface OrdinalRecord {
  canonical_ref: string;
  cursor: number;
}

/** The carrier fields the 1e/1f input contract requires on every status
 * change. Both fields REQUIRED (not optional) — see module doc above. */
export interface CarrierFields {
  supersedes_ref: string;
  change_ordinal: number;
}

async function getOrdinalRecord(canonical_ref: string): Promise<OrdinalRecord | undefined> {
  const db = await openOutboxDB();
  return db.get("ordinals", canonical_ref);
}

async function putOrdinalRecord(rec: OrdinalRecord): Promise<void> {
  const db = await openOutboxDB();
  await db.put("ordinals", rec);
}

/**
 * Allocates the next `change_ordinal` for `canonical_ref`, durably
 * advancing this device's local cursor. `highestInCollapsedRead` is the
 * highest `change_ordinal` this device has observed for the canonical in
 * its most recent collapsed read (0 if the canonical has no carriers yet —
 * the base row's implicit ordinal).
 *
 * §4.6's accepted residual risk (noted, not fixed here): two devices
 * offline-allocating for the SAME canonical concurrently can mint a tying
 * ordinal server-side; the 1e/1f fold's INV-4 tiebreak (ts, then
 * capture_id) resolves it deterministically, it is simply not
 * PWA-side-avoidable without a round-trip this design intentionally does
 * not require.
 */
export async function allocateChangeOrdinal(
  canonical_ref: string,
  highestInCollapsedRead: number,
): Promise<number> {
  const existing = await getOrdinalRecord(canonical_ref);
  const deviceCursor = existing?.cursor ?? 0;
  const next = Math.max(highestInCollapsedRead, deviceCursor) + 1;
  await putOrdinalRecord({ canonical_ref, cursor: next });
  return next;
}

/**
 * The single locus that constructs `CarrierFields` — the ONLY way to
 * obtain a `supersedes_ref`+`change_ordinal` pair anywhere in this
 * codebase. Consulting the cursor is mandatory: there is no path to a
 * `CarrierFields` value that skips `allocateChangeOrdinal`.
 */
export async function buildCarrierFields(
  canonical_ref: string,
  highestInCollapsedRead: number,
): Promise<CarrierFields> {
  const change_ordinal = await allocateChangeOrdinal(canonical_ref, highestInCollapsedRead);
  return { supersedes_ref: canonical_ref, change_ordinal };
}

/** Test-only: resets a canonical's device cursor so tests don't leak
 * cross-canonical-id namespace collisions between runs sharing a fake-IDB
 * instance. Mirrors db.ts's own `_reset*ForTests` convention. */
export async function _resetOrdinalForTests(canonical_ref: string): Promise<void> {
  const db = await openOutboxDB();
  await db.delete("ordinals", canonical_ref);
}
